/**
 * Hàng danh sách BĐS cuộn ngang — dùng trên trang chủ và trang chi tiết.
 *
 * Luồng dữ liệu:
 * - Nhận mảng properties hoặc isLoading từ parent.
 * - Theo dõi scrollLeft để bật/tắt nút prev/next.
 * - scrollByAmount: cuộn mượt theo bước card qua smoothScrollHorizontal.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PropertyCard } from '../property/PropertyCard';
import { PropertyCardSkeleton } from '../property/PropertyCardSkeleton';
import { TEXT } from '../../shared/languages';
import {
  getPagedScrollTarget,
  smoothScrollHorizontal,
} from '../../shared/utils/smoothHorizontalScroll';

const CARD_WIDTH = 244;
const CARD_GAP = 20;
const CARD_STEP = CARD_WIDTH + CARD_GAP;

export const ListingRow = ({ title, href, properties = [], isLoading = false, className = '' }) => {
  const scrollRef = useRef(null);
  /** Hủy animation cuộn đang chạy khi người dùng bấm liên tiếp */
  const cancelScrollRef = useRef(null);
  /** Có thể cuộn sang trái/phải — cập nhật theo scrollLeft */
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /** Tính lại trạng thái nút điều hướng dựa trên vị trí cuộn hiện tại */
  const updateScrollState = () => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    setCanScrollLeft(element.scrollLeft > 4);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 4);
  };

  /** Khởi tạo và lắng nghe resize khi danh sách hoặc trạng thái loading thay đổi */
  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.removeEventListener('resize', updateScrollState);
      cancelScrollRef.current?.();
    };
  }, [properties, isLoading]);

  /** Cuộn theo hướng (-1 trái, 1 phải), tính bước từ kích thước card thực tế */
  const scrollByAmount = (direction) => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    cancelScrollRef.current?.();

    const firstItem = element.querySelector('.listing-row-item');
    const itemStep = firstItem
      ? firstItem.getBoundingClientRect().width + CARD_GAP
      : CARD_STEP;
    const targetLeft = getPagedScrollTarget(element, direction, itemStep);

    cancelScrollRef.current = smoothScrollHorizontal(element, targetLeft, {
      duration: 520,
      onComplete: updateScrollState,
    });
  };

  if (!isLoading && !properties.length) {
    return null;
  }

  const showNav = !isLoading && properties.length > 1;

  return (
    <section className={`listing-row ${className}`.trim()}>
      <div className="listing-row-top">
        {href ? (
          <Link to={href} className="listing-row-header">
            <h2>{title}</h2>
            <span className="listing-row-chevron" aria-hidden="true">
              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M8 16h14" strokeLinecap="round" />
                <path d="M18 11l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Link>
        ) : (
          <div className="listing-row-header">
            <h2>{title}</h2>
          </div>
        )}

        {showNav && (
          <div className="listing-row-nav">
            <button
              type="button"
              className="listing-row-nav-btn"
              aria-label={TEXT.common.previous}
              disabled={!canScrollLeft}
              onClick={() => scrollByAmount(-1)}
            >
              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 8l-8 8 8 8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="listing-row-nav-btn"
              aria-label={TEXT.common.next}
              disabled={!canScrollRight}
              onClick={() => scrollByAmount(1)}
            >
              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 8l8 8-8 8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div
        className="listing-row-track"
        ref={scrollRef}
        onScroll={updateScrollState}
      >
        {isLoading
          ? Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="listing-row-item">
                <PropertyCardSkeleton />
              </div>
            ))
          : properties.map((property) => (
              <div key={property.id} className="listing-row-item">
                <PropertyCard property={property} layout="row" />
              </div>
            ))}
      </div>
    </section>
  );
};
